import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import { useDatadog } from '~~/layers/base/composables/useDatadog'
import { useAuthAccsService } from '~~/layers/base/services/authAccs.service'
import { useCustomerAccsService } from '~~/layers/base/services/customerAccs.service'
import { useCustomerCartAccsService } from '~~/layers/base/services/customer_cart_accs.service'
import type {
    Login,
    SignUpWithSubscription,
    ForgotPassword,
    ResetPasswordWithOtpPayload,
    AuthToken,
} from '@/types/auth'

export const useCustomerAuthAccsStore = defineStore('customerAuthAccs', () => {
    const datadog = useDatadog()
    const { getAuthCustomer, setAuthCustomer, resetAuthCustomer } = useAuthCustomerAccs()
    const customerStore = useCustomerStore()
    const { getCustomer } = customerStore
    const { doLogin, doSignup, doSendOtp, doResetPasswordWithOtp } = useAuthAccsService()
    const { doUpdateSubscription } = useCustomerAccsService()
    const { convertGuestToCustomerCart } = useCustomerCartAccsService()
    // State
    const inProgress: Ref<boolean> = ref(false)
    const customerAuth: Ref<AuthToken | null> = ref(getAuthCustomer())
    const errorMessage: Ref<string | null> = ref(null) // Added to track login errors
    // Actions
    const login = async (payload: Login): Promise<void> => {
        try {
            errorMessage.value = null
            inProgress.value = true
            const { data, error } = await doLogin({
                email: payload.email,
                password: payload.password,
            })

            if (data?.value) {
                loginSuccess(data.value)
                await getCustomer()

                // Handle cart conversion with proper error handling
                try {
                    await convertGuestToCustomerCart()
                } catch (cartError) {
                    // Report cart conversion failure to Datadog
                    const errorObj =
                        cartError instanceof Error ? cartError : new Error(String(cartError))
                    datadog.logError(errorObj, {
                        errorType: 'convert_guest_to_customer_cart_failed',
                        context: 'login_convert_guest_to_customer_cart_failed',
                    })
                }
            } else if (error?.value) {
                errorMessage.value = 'Incorrect password. Please try again.'
                throw errorMessage.value
            }
        } finally {
            inProgress.value = false
        }
    }

    const loginSuccess = (token: AuthToken): void => {
        customerAuth.value = token
        setAuthCustomer(token)
        // setTimeout(() => { // errorRecoveryStore.retryFailedActions() // }, 400) // redirectToInterruptedPage()
    }

    const signUp = async (payload: SignUpWithSubscription): Promise<void> => {
        errorMessage.value = null
        inProgress.value = true
        const { error } = await doSignup({ ...payload })

        if (error?.value) {
            inProgress.value = false
            errorMessage.value = error.value.message
            throw error.value
        } else {
            await login({
                email: payload.email,
                password: payload.password,
            })
            await doUpdateSubscription({
                ...payload,
            })
        }
    }

    // Actions
    const sendOtp = async (payload: ForgotPassword): Promise<void> => {
        try {
            inProgress.value = true
            const { error } = await doSendOtp({
                email: payload.email,
            })

            if (error.value) {
                throw error.value
            }
        } finally {
            inProgress.value = false
        }
    }

    const resetPasswordWithOtp = async (payload: ResetPasswordWithOtpPayload): Promise<void> => {
        try {
            inProgress.value = true
            const { error } = await doResetPasswordWithOtp({
                otp: payload.otp,
                email: payload.email,
                password: payload.password,
            })

            if (error.value) {
                throw error.value
            }
        } finally {
            inProgress.value = false
        }
    }

    const logout = (): void => {
        customerAuth.value = null
        resetAuthCustomer()
    }

    const resetState = (): void => {
        inProgress.value = false
    }

    return {
        // State
        inProgress,
        customerAuth,
        errorMessage,
        // Actions
        login,
        loginSuccess,
        logout,
        signUp,
        sendOtp,
        resetState,
        resetPasswordWithOtp,
    }
})
